import moment from 'moment'
import { brandColors } from 'transfix-ui/theme/colors'
import { IFullStop } from 'types'
import { TRACKING_METHOD_TYPE } from 'types/tracking'
import { LOAD_STATE } from 'apps/fleet-planner/constants/loads'
import { ILocationELD, IShipmentETA, ITruck } from 'apps/fleet-planner/types'
import { capitalize } from 'utils/capitalize'
import { FMS_ELD_ERRORS } from './ELDErrors'
import { FMS_ELD_STATES, IFMSELDError, IFMSELDState } from './ELDStates'

/**
 *
 * @param trackingMethod the selected tracking method of the shipment
 * @returns 'Transfix App' if the tracking method is 'mobile', or a string removing all underscores
 */
const TRACKING_METHODS_BY_NAME = {
  mobile: 'Transfix app',
  project_44: 'Project 44',
  keep_truckin: 'KeepTruckin',
  fms_mobile: 'Transfix app',
}

export const getTrackingMethodText = (trackingMethod: TRACKING_METHOD_TYPE | null): string => {
  if (!trackingMethod) {
    return ''
  }
  // If tracking method is not present in `TRACKING_METHODS_BY_NAME`
  if (
    !Object.keys({
      ...TRACKING_METHODS_BY_NAME,
    }).includes(trackingMethod)
  ) {
    return capitalize(trackingMethod.replaceAll('_', ' '))
  }

  return TRACKING_METHODS_BY_NAME[trackingMethod]
}

/**
 * Subset of `Not Set up to Track` errors
 * @param recentLocations
 * @param trackingMethod
 * @param isKeepTruckinEnabled
 * @param keepTruckinId
 * @returns the error message to display when the state of load tracking is `Not Set up to Track`
 */

export interface ELDErrorProps {
  isKeepTruckinEnabled: boolean | undefined
  isP44Enabled: boolean | undefined
  p44IsTruckNumberValid: boolean | undefined
  p44NeedsValidTruckNumber: boolean | undefined
  trackingMethod: TRACKING_METHOD_TYPE | undefined
  truck: ITruck | null | undefined
}

export interface ELDStateProps {
  recentLocations: ILocationELD[]
  status: LOAD_STATE
  trackingMethod: TRACKING_METHOD_TYPE | undefined
  isKeepTruckinEnabled: boolean | undefined
  isP44Enabled: boolean | undefined
  truck: Partial<ITruck> | null | undefined
  p44NeedsValidTruckNumber: boolean | undefined
  p44isTruckNumberValid: boolean | undefined
  isCurrentShipment?: boolean
  shipmentETA?: IShipmentETA
  firstPickup: IFullStop | undefined
  globalDefinitionOfTrackingEnabled: boolean
  isTracking?: boolean
}

export const getELDError = (eldProps: ELDErrorProps): IFMSELDError | undefined => {
  const {
    isKeepTruckinEnabled,
    isP44Enabled,
    p44IsTruckNumberValid,
    p44NeedsValidTruckNumber,
    trackingMethod,
    truck,
  } = eldProps || {}

  const { keepTruckinId } = truck || {}

  if (trackingMethod === TRACKING_METHOD_TYPE.keepTruckin && !isKeepTruckinEnabled) {
    return FMS_ELD_ERRORS.keepTruckinCarrierNotConnected
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.keepTruckin && !truck) {
    return FMS_ELD_ERRORS.keepTruckinTruckNotEntered
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.keepTruckin && !keepTruckinId) {
    return FMS_ELD_ERRORS.keepTruckinTruckNotFound
  }

  if (trackingMethod === TRACKING_METHOD_TYPE.project44 && !isP44Enabled) {
    return FMS_ELD_ERRORS.project44CarrierNotConnected
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.project44 && p44NeedsValidTruckNumber) {
    return FMS_ELD_ERRORS.p44TruckNotEntered
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.project44 && !p44IsTruckNumberValid) {
    return FMS_ELD_ERRORS.p44TruckNotFound
  }

  return undefined
}

/**
 * @returns The current state of the Load Details ELD tracking map, including what messages to display
 */

export const getELDState = (eldProps: ELDStateProps): IFMSELDState => {
  const {
    recentLocations = [],
    status,
    trackingMethod,
    isKeepTruckinEnabled,
    isP44Enabled,
    truck,
    p44NeedsValidTruckNumber,
    p44isTruckNumberValid,
    isCurrentShipment,
    shipmentETA,
    firstPickup,
    globalDefinitionOfTrackingEnabled,
    isTracking,
  } = eldProps || {}
  const { keepTruckinId } = truck || {}
  const { onTime = true } = shipmentETA || {}
  const { appointment } = firstPickup || {}
  const { startAt: pickupStartTime } = appointment || {}
  const [lastPing] = recentLocations || []

  // the following conditions indicate missing params for tracking
  const KTNotSetUpToTrack = !isKeepTruckinEnabled || !truck || !keepTruckinId
  const p44NotSetUpToTrack = !isP44Enabled || p44NeedsValidTruckNumber || !p44isTruckNumberValid

  // the following conditions mean that a shipment meets all conditions to track
  const KTSetUpToTrack = trackingMethod === TRACKING_METHOD_TYPE.keepTruckin && !KTNotSetUpToTrack
  const p44SetUpToTrack = trackingMethod === TRACKING_METHOD_TYPE.project44 && !p44NotSetUpToTrack

  const now = moment()
  const isMoreThanTwoHoursAgo = moment(pickupStartTime).diff(now, 'hours') >= 2
  const trackingStopped = globalDefinitionOfTrackingEnabled
    ? !!lastPing && !isTracking
    : !!lastPing && moment().diff(moment(lastPing.locatedAt), 'hours') > 2
  const noPings = recentLocations.length === 0

  // cases where carrier is not set up to track
  if (trackingMethod === TRACKING_METHOD_TYPE.keepTruckin && KTNotSetUpToTrack) {
    return FMS_ELD_STATES.notSetUpToTrack
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.mobile && noPings) {
    return FMS_ELD_STATES.notSetUpToTrackMobile
  }
  if (trackingMethod === TRACKING_METHOD_TYPE.project44 && p44NotSetUpToTrack) {
    return FMS_ELD_STATES.notSetUpToTrack
  }

  // case where the selected truck is currently delivering another load
  if (status === LOAD_STATE['Ready for Dispatch'] && isCurrentShipment === false) {
    return FMS_ELD_STATES.deliveringAnotherLoad
  }

  // in the case of P44, if the PU is >2h away, no pings will be displayed
  if (
    p44SetUpToTrack &&
    noPings &&
    isMoreThanTwoHoursAgo &&
    status !== LOAD_STATE['Ready for Dispatch']
  ) {
    return FMS_ELD_STATES.p44LocationNotAvailableYet
  }

  // if there are no pings but the carrier is set up to track, display a info message
  // telling them there are currently no active pings
  if (
    status !== LOAD_STATE['Ready for Dispatch'] &&
    (KTSetUpToTrack || p44SetUpToTrack) &&
    noPings
  ) {
    return FMS_ELD_STATES.noPings
  }

  // cases where carrier is set up to track and needs to be dispatched
  if (KTSetUpToTrack && status === LOAD_STATE['Ready for Dispatch']) {
    return FMS_ELD_STATES.setUpToTrack
  }
  // mobile is set up if there's at least one location ping that came from a mobile device
  if (
    trackingMethod === TRACKING_METHOD_TYPE.mobile &&
    recentLocations.some(location => location.locationSource === TRACKING_METHOD_TYPE.mobile) &&
    status === LOAD_STATE['Ready for Dispatch']
  ) {
    return FMS_ELD_STATES.setUpToTrack
  }
  if (p44SetUpToTrack && status === LOAD_STATE['Ready for Dispatch']) {
    return FMS_ELD_STATES.setUpToTrack
  }

  // When there are ony stale pings on a *moving* shipment, we consider the tracking to have stopped
  if (
    !!trackingMethod &&
    status !== LOAD_STATE['Ready for Dispatch'] &&
    status !== LOAD_STATE.Delivered &&
    trackingStopped
  ) {
    return FMS_ELD_STATES.trackingStopped
  }

  /**
   * General cases
   */

  if ((status === LOAD_STATE['In Transit'] || status === LOAD_STATE.Dispatched) && onTime) {
    return FMS_ELD_STATES.inTransitOnTime
  }
  if ((status === LOAD_STATE['In Transit'] || status === LOAD_STATE.Dispatched) && !onTime) {
    return FMS_ELD_STATES.inTransitLate
  }
  if (status === LOAD_STATE['At Pickup'] || status === LOAD_STATE['At Dropoff']) {
    return FMS_ELD_STATES.atPickupDropoff
  }
  if (status === LOAD_STATE.Delivered) {
    return FMS_ELD_STATES.delivered
  }

  return FMS_ELD_STATES.notSetUpToTrack
}

/**
 * Returns BG color depending on ELD state
 */
export const getBackgroundColor = (alertType?: string) => {
  switch (alertType) {
    case 'error':
      return brandColors.errorLight
    case 'warning':
      return brandColors.warningLight
    case 'info':
      return brandColors.infoLight
    default:
      return brandColors.coolGray1
  }
}

/**
 * Returns Title color depending on ELD state
 */
export const getTitleColor = (alertType?: string) => {
  switch (alertType) {
    case 'error':
      return brandColors.error
    case 'warning':
      return brandColors.warning
    case 'info':
      return brandColors.info
    default:
      return brandColors.coolGray8
  }
}
